/**
 * Platform — NOT FSD Part 16. FSD Part 16 is Cross-Module Integration:
 * requirements only, no screens of its own. This file holds the screens for
 * FSD Part D's cross-cutting specifications (sections D.2–D.9.2, kind
 * `crosscut`), FSD Part 17 (AI, kind `catalogue`) and platform administration
 * (users, roles — a recorded deviation, see below).
 *
 * Item ids are stable: favourites and recents (localStorage) key off them, so
 * an item may move between sections or parts but its id never changes.
 */
import type { NavGroup } from '../types'
import { PERMISSIONS } from '@/lib/permissions'
import { ATTENTION_PERMISSIONS } from '@/modules/attention/permissions'
import { AUTHORITY_PERMISSIONS } from '@/modules/authority/permissions'
import { RULES_PERMISSIONS } from '@/modules/rules/permissions'
import { REPORTING_PERMISSIONS } from '@/modules/reporting/permissions'
import { AI_PERMISSIONS } from '@/modules/ai/permissions'
import { PARTY_PERMISSIONS } from '@/modules/party/permissions'

export const P16_PLATFORM_GROUPS: NavGroup[] = [
  {
    id: 's-D.2',
    kind: 'crosscut',
    fsd: 'D.2',
    labelKey: 'nav.fsd.sD_2.short',
    fullTitleKey: 'nav.fsd.sD_2.full',
    items: [
      // recorded deviation: no FSD spec for user administration; FSD wants
      // position-derived access (XIN-FR-020 / FND-FR-083), not built yet —
      // DECISIONS.md C17
      {
        id: 'users',
        labelKey: 'nav.items.users.label',
        to: '/users',
        icon: 'users',
        permission: PERMISSIONS.userList,
        keywordsKey: 'nav.items.users.keywords',
        descriptionKey: 'nav.items.users.description',
        fsd: 'D.2',
        fsdStatus: 'extraneous',
      },
      // recorded deviation: no FSD spec for user administration; FSD wants
      // position-derived access (XIN-FR-020 / FND-FR-083), not built yet —
      // DECISIONS.md C17
      {
        id: 'roles',
        labelKey: 'nav.items.roles.label',
        to: '/roles',
        icon: 'shield',
        permission: PERMISSIONS.roleList,
        keywordsKey: 'nav.items.roles.keywords',
        descriptionKey: 'nav.items.roles.description',
        fsd: 'D.2',
        fsdStatus: 'extraneous',
      },
    ],
  },
  {
    id: 's-D.3',
    kind: 'crosscut',
    fsd: 'D.3',
    labelKey: 'nav.fsd.sD_3.short',
    fullTitleKey: 'nav.fsd.sD_3.full',
    items: [
      {
        id: 'audit',
        labelKey: 'nav.items.audit.label',
        to: '/audit',
        icon: 'history',
        permission: PERMISSIONS.auditRead,
        keywordsKey: 'nav.items.audit.keywords',
        descriptionKey: 'nav.items.audit.description',
        code: 'XFN-AU-002',
      },
    ],
  },
  {
    id: 's-D.4',
    kind: 'crosscut',
    fsd: 'D.4',
    labelKey: 'nav.fsd.sD_4.short',
    fullTitleKey: 'nav.fsd.sD_4.full',
    items: [
      // C1 (design §10.4): how each kind of attention alert is delivered —
      // bell, email or digest.
      {
        id: 'attention-policies',
        labelKey: 'attention.routeTitles.policies',
        to: '/settings/notification-policies',
        icon: 'mail',
        permission: ATTENTION_PERMISSIONS.policyManage,
        descriptionKey: 'nav.items.attentionPolicies.description',
        code: 'XFN-NT-004',
      },
    ],
  },
  {
    id: 's-D.5',
    kind: 'crosscut',
    fsd: 'D.5',
    labelKey: 'nav.fsd.sD_5.short',
    fullTitleKey: 'nav.fsd.sD_5.full',
    items: [
      {
        id: 'reporting-saved-filters',
        labelKey: 'reporting.routeTitles.savedFilters',
        to: '/reporting/saved-filters',
        icon: 'star',
        permission: REPORTING_PERMISSIONS.filterManage,
        keywordsKey: 'nav.items.reportingSavedFilters.keywords',
        descriptionKey: 'nav.items.reportingSavedFilters.description',
        code: 'XFN-UX-008',
      },
    ],
  },
  {
    id: 's-D.6',
    kind: 'crosscut',
    fsd: 'D.6',
    labelKey: 'nav.fsd.sD_6.short',
    fullTitleKey: 'nav.fsd.sD_6.full',
    items: [
      {
        id: 'party-parties',
        labelKey: 'party.nav.parties',
        to: '/party/parties',
        icon: 'users',
        permission: PARTY_PERMISSIONS.read,
        descriptionKey: 'nav.items.partyParties.description',
        code: 'XFN-MD-003',
      },
      {
        id: 'party-merges',
        labelKey: 'party.nav.merges',
        to: '/party/merges',
        icon: 'refresh',
        permission: PARTY_PERMISSIONS.read,
        descriptionKey: 'nav.items.partyMerges.description',
        code: 'XFN-MD-003',
      },
      // party-dsr and party-retention are declared in the module's own routes
      // and PartyNav.vue but are NOT given a top-level entry here: C5-P4 (DSR,
      // retention, erasure, export, the 360) was never dispatched in this fused
      // workflow, so `party.dsr.read`/`party.retention.read` are not registered
      // by any package and no role — not even Tenant Admin — holds them
      // (auth.can() is false for everyone). Listing them here would put a link
      // in the sidebar that renders for nobody; the module's own sub-nav is
      // already correctly gated the same way and needs no wiring-side duplicate.
    ],
  },
  {
    id: 's-D.8',
    kind: 'crosscut',
    fsd: 'D.8',
    labelKey: 'nav.fsd.sD_8.short',
    fullTitleKey: 'nav.fsd.sD_8.full',
    items: [
      {
        id: 'reporting-daily-operating',
        labelKey: 'reporting.routeTitles.dailyOperating',
        to: '/reporting/daily-operating',
        icon: 'history',
        permission: REPORTING_PERMISSIONS.dailyOpsRead,
        keywordsKey: 'nav.items.reportingDailyOperating.keywords',
        descriptionKey: 'nav.items.reportingDailyOperating.description',
        code: 'XFN-RP-008',
      },
      {
        id: 'reporting-board',
        labelKey: 'reporting.routeTitles.board',
        to: '/reporting/daily-operating-board',
        icon: 'calendar',
        permission: REPORTING_PERMISSIONS.dailyOpsRead,
        keywordsKey: 'nav.items.reportingBoard.keywords',
        descriptionKey: 'nav.items.reportingBoard.description',
        code: 'XFN-RP-008',
      },
      {
        id: 'reporting-exception-views',
        labelKey: 'reporting.routeTitles.exceptionViews',
        to: '/reporting/exception-views',
        icon: 'alert',
        permission: REPORTING_PERMISSIONS.exceptionViewRead,
        keywordsKey: 'nav.items.reportingExceptionViews.keywords',
        descriptionKey: 'nav.items.reportingExceptionViews.description',
        code: 'XFN-RP-009',
      },
      // `reporting.routeTitles.dashboards` reads "Role dashboards" rather than
      // "Dashboards" because the app's home page is already called Dashboard,
      // and two sidebar entries a keyword search cannot tell apart is exactly
      // the collision `keys.test.ts` was written about.
      // re-homed to D.8 Reporting, Metric Governance and the Enterprise View, C17
      {
        id: 'reporting-dashboards',
        labelKey: 'reporting.routeTitles.dashboards',
        to: '/reporting/dashboards',
        icon: 'dashboard',
        permission: REPORTING_PERMISSIONS.dashboardRead,
        keywordsKey: 'nav.items.reportingDashboards.keywords',
        descriptionKey: 'nav.items.reportingDashboards.description',
      },
    ],
  },
  // The exception, override and reason-code framework (design §8, §5.9).
  // `/controls`, not `/settings` — these are worked, not configured, per
  // rules/routes.ts's own comment.
  {
    id: 's-D.9.1',
    kind: 'crosscut',
    fsd: 'D.9.1',
    code: 'XFN-F01',
    labelKey: 'nav.fsd.sD_9_1.short',
    fullTitleKey: 'nav.fsd.sD_9_1.full',
    items: [
      {
        id: 'rules-controls',
        labelKey: 'rules.routeTitles.controls',
        to: '/controls/rules',
        icon: 'shield',
        permission: RULES_PERMISSIONS.controlRead,
        descriptionKey: 'nav.items.rulesControls.description',
      },
      {
        id: 'rules-overrides',
        labelKey: 'rules.routeTitles.overrides',
        to: '/controls/overrides',
        icon: 'history',
        permission: RULES_PERMISSIONS.overrideRead,
        descriptionKey: 'nav.items.rulesOverrides.description',
        fsd: 'D.9.1.8',
      },
      {
        id: 'rules-queues',
        labelKey: 'rules.routeTitles.queues',
        to: '/controls/queues',
        icon: 'alert',
        permission: RULES_PERMISSIONS.queueRead,
        descriptionKey: 'nav.items.rulesQueues.description',
        // XFN-EX-005 is a D.1 requirement; the screen sits with the D.9.1 spec
        // that operates it, but its chip names the requirement's own section.
        fsd: 'D.1',
        code: 'XFN-EX-005',
      },
    ],
  },
  // The enterprise authority framework (design §4, §5.9): delegation, absence
  // reassignment, materiality thresholds for a fresh approval.
  {
    id: 's-D.9.2',
    kind: 'crosscut',
    fsd: 'D.9.2',
    code: 'XFN-F02',
    labelKey: 'nav.fsd.sD_9_2.short',
    fullTitleKey: 'nav.fsd.sD_9_2.full',
    items: [
      {
        id: 'authority-delegations',
        labelKey: 'authority.routeTitles.delegations',
        to: '/authority/delegations',
        icon: 'users',
        permission: AUTHORITY_PERMISSIONS.delegationRead,
        descriptionKey: 'nav.items.authorityDelegations.description',
      },
      {
        id: 'authority-absences',
        labelKey: 'authority.routeTitles.absences',
        to: '/authority/absences',
        icon: 'user',
        permission: AUTHORITY_PERMISSIONS.absenceRead,
        descriptionKey: 'nav.items.authorityAbsences.description',
      },
      {
        id: 'authority-materiality',
        labelKey: 'authority.routeTitles.materiality',
        to: '/authority/materiality',
        icon: 'scale',
        permission: AUTHORITY_PERMISSIONS.matrixRead,
        descriptionKey: 'nav.items.authorityMateriality.description',
      },
    ],
  },
  // FSD Part 17 (AI): the AI layer's non-chat screens — the Assistant itself
  // lives in the right rail (CLAUDE.md §11), never here. The whole section
  // hides when AI is disabled: nobody holds an `ai.*` permission, so every
  // item below is unreachable by the ordinary permission filter.
  {
    id: 's-17',
    kind: 'catalogue',
    fsd: '17',
    labelKey: 'nav.fsd.s17.short',
    fullTitleKey: 'nav.fsd.s17.full',
    items: [
      {
        id: 'ai-briefings',
        labelKey: 'ai.routeTitles.briefings',
        to: '/ai/briefings',
        icon: 'star',
        permission: AI_PERMISSIONS.agentRead,
        keywordsKey: 'nav.items.aiBriefings.keywords',
        descriptionKey: 'nav.items.aiBriefings.description',
        code: 'AI-FR-031',
      },
      {
        id: 'ai-recommendations',
        labelKey: 'ai.routeTitles.ledger',
        to: '/ai/recommendations',
        icon: 'inbox',
        permission: AI_PERMISSIONS.recommendationRead,
        keywordsKey: 'nav.items.aiRecommendations.keywords',
        descriptionKey: 'nav.items.aiRecommendations.description',
        code: 'AI-FR-035',
      },
      {
        id: 'ai-config-anomalies',
        labelKey: 'ai.routeTitles.anomalies',
        to: '/ai/config-anomalies',
        icon: 'shield',
        permission: AI_PERMISSIONS.anomalyRead,
        keywordsKey: 'nav.items.aiConfigAnomalies.keywords',
        descriptionKey: 'nav.items.aiConfigAnomalies.description',
        code: 'AI-FR-030',
      },
      {
        id: 'ai-autonomy',
        labelKey: 'ai.routeTitles.autonomy',
        to: '/ai/autonomy',
        icon: 'settings',
        permission: AI_PERMISSIONS.capabilityRead,
        keywordsKey: 'nav.items.aiAutonomy.keywords',
        descriptionKey: 'nav.items.aiAutonomy.description',
      },
    ],
  },
]
